import { useMemo, useRef, useState } from 'react';
import { Navigation } from '../components/Navigation';
import { FileList, buildFolderPathMap } from '../components/FileList';
import { UploadZone } from '../components/UploadZone';
import { useFiles, useFolders, useCreateFolder, useSearchFiles, useAllFolders } from '../hooks/useFiles';
import { useUpload } from '../hooks/useUpload';

const s = {
  layout: { display: 'flex', flexDirection: 'column' as const, minHeight: '100vh', background: '#f9fafb' } as const,
  main: { flex: 1, padding: '2rem 1.5rem', maxWidth: '1100px', width: '100%', margin: '0 auto', boxSizing: 'border-box' as const } as const,
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const } as const,
  toolbar: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' as const } as const,
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', color: '#6b7280', flexWrap: 'wrap' as const } as const,
  crumbLink: { color: '#1a1a1a', textDecoration: 'underline', cursor: 'pointer' } as const,
  card: { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '1.25rem' } as const,
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', fontWeight: 600, fontSize: '0.875rem', color: '#374151' } as const,
  countBadge: { fontWeight: 400 as const, color: '#9ca3af', fontSize: '0.8rem' } as const,
  btn: { padding: '0.45rem 1rem', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500 } as const,
  secBtn: { padding: '0.45rem 1rem', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0 } as const,
  input: { padding: '0.45rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.875rem' } as const,
  uploadBtnWrap: { position: 'relative' as const } as const,
  uploadBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 1rem', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' as const, flexShrink: 0 } as const,
  uploadBadge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', padding: '0 5px', background: '#fff', color: '#1a1a1a', borderRadius: '9px', fontSize: '0.7rem', fontWeight: 700 } as const,
  pulseDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'pulse-dot 1.2s ease-in-out infinite' } as const,
  uploadOverlay: { position: 'fixed' as const, inset: 0, zIndex: 40 } as const,
  uploadPanel: { position: 'absolute' as const, top: 'calc(100% + 8px)', right: 0, zIndex: 50, width: '380px', maxWidth: '80vw', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '1rem', boxSizing: 'border-box' as const } as const,
  uploadPanelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' } as const,
  uploadPanelTitle: { fontWeight: 600 as const, fontSize: '0.875rem', color: '#374151' } as const,
  uploadCloseBtn: { border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#9ca3af', lineHeight: 1 } as const,
  dropOverlay: { position: 'absolute' as const, inset: 0, background: 'rgba(249,250,251,0.94)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 600 as const, color: '#1a1a1a', zIndex: 5, pointerEvents: 'none' as const, borderRadius: '8px' } as const,
};

interface BreadcrumbItem { id: string | null; name: string; }

export function DashboardPage(): JSX.Element {
  const [folderStack, setFolderStack] = useState<BreadcrumbItem[]>([{ id: null, name: 'マイドライブ' }]);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const currentFolderId = folderStack[folderStack.length - 1].id;
  const isSearching = searchTerm.trim().length > 0;

  const filesQuery = useFiles(currentFolderId);
  const foldersQuery = useFolders(currentFolderId);
  const searchQuery = useSearchFiles(searchTerm);
  const allFoldersQuery = useAllFolders();
  const folderPathMap = useMemo(
    () => buildFolderPathMap(allFoldersQuery.data?.folders ?? []),
    [allFoldersQuery.data],
  );
  const createFolder = useCreateFolder();
  const { uploads, uploadFiles, clearCompleted, resolveConflict } = useUpload(currentFolderId);
  const activeUploadCount = uploads.filter((u) => u.status === 'uploading').length;

  const [isDraggingOverList, setIsDraggingOverList] = useState(false);
  const listDragCounter = useRef(0);

  const handleListDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    listDragCounter.current += 1;
    setIsDraggingOverList(true);
  };
  const handleListDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleListDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    listDragCounter.current = Math.max(0, listDragCounter.current - 1);
    if (listDragCounter.current === 0) setIsDraggingOverList(false);
  };
  const handleListDrop = (e: React.DragEvent) => {
    e.preventDefault();
    listDragCounter.current = 0;
    setIsDraggingOverList(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      uploadFiles(dropped);
      setUploadPanelOpen(true);
    }
  };

  const openFolder = (id: string) => {
    const folder = foldersQuery.data?.folders.find((f) => f.id === id);
    if (folder) setFolderStack((prev) => [...prev, { id, name: folder.name }]);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await createFolder.mutateAsync({ name: newFolderName.trim(), parentFolderId: currentFolderId });
    setNewFolderName('');
    setNewFolderMode(false);
  };

  return (
    <div style={s.layout}>
      <Navigation />
      <div style={s.main}>

        {/* Breadcrumb + Toolbar */}
        <div style={s.headerRow}>
          {isSearching ? (
            <div style={s.breadcrumb}>
              <span style={{ fontWeight: 600, color: '#111827' }}>検索結果</span>
            </div>
          ) : (
            <div style={s.breadcrumb}>
              {folderStack.map((crumb, idx) => (
                <span key={crumb.id ?? 'root'} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {idx > 0 && <span>/</span>}
                  {idx < folderStack.length - 1 ? (
                    <span style={s.crumbLink} onClick={() => setFolderStack((p) => p.slice(0, idx + 1))}>{crumb.name}</span>
                  ) : (
                    <span style={{ fontWeight: 600, color: '#111827' }}>{crumb.name}</span>
                  )}
                </span>
              ))}
            </div>
          )}

          <div style={s.toolbar}>
            <input
              style={s.input}
              placeholder="ファイルを検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {!isSearching && (
              newFolderMode ? (
                <>
                  <input style={s.input} placeholder="フォルダ名" value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setNewFolderMode(false); }}
                    autoFocus />
                  <button style={s.btn} onClick={handleCreateFolder} disabled={createFolder.isPending}>
                    {createFolder.isPending ? '作成中…' : '作成'}
                  </button>
                  <button style={s.secBtn} onClick={() => setNewFolderMode(false)}>キャンセル</button>
                </>
              ) : (
                <button style={s.secBtn} onClick={() => setNewFolderMode(true)}>+ 新しいフォルダ</button>
              )
            )}

            <div style={s.uploadBtnWrap}>
              <button style={s.uploadBtn} onClick={() => setUploadPanelOpen((o) => !o)}>
                <span>アップロード</span>
                {activeUploadCount > 0 && (
                  <>
                    <span style={s.uploadBadge}>{activeUploadCount}</span>
                    <span style={s.pulseDot} />
                  </>
                )}
              </button>

              {uploadPanelOpen && (
                <>
                  <div style={s.uploadOverlay} onClick={() => setUploadPanelOpen(false)} />
                  <div style={s.uploadPanel} onClick={(e) => e.stopPropagation()}>
                    <div style={s.uploadPanelHeader}>
                      <span style={s.uploadPanelTitle}>ファイルをアップロード</span>
                      <button style={s.uploadCloseBtn} onClick={() => setUploadPanelOpen(false)}>閉じる</button>
                    </div>
                    <UploadZone uploads={uploads} onUpload={uploadFiles} onClear={clearCompleted} onResolveConflict={resolveConflict} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Files & Folders */}
        <div
          style={{ ...s.card, position: 'relative', outline: isDraggingOverList ? '2px dashed #1a1a1a' : '2px dashed transparent', outlineOffset: '-2px' }}
          onDragEnter={handleListDragEnter}
          onDragOver={handleListDragOver}
          onDragLeave={handleListDragLeave}
          onDrop={handleListDrop}
        >
          {isDraggingOverList && <div style={s.dropOverlay}>ここにドロップしてアップロード</div>}
          <div style={s.cardHeader}>
            <span>{isSearching ? '検索結果' : 'ファイル一覧'}</span>
            <span style={s.countBadge}>
              {isSearching
                ? (searchQuery.data?.files.length ?? 0)
                : (foldersQuery.data?.folders.length ?? 0) + (filesQuery.data?.files.length ?? 0)} 件
            </span>
          </div>
          <FileList
            folders={isSearching ? [] : (foldersQuery.data?.folders ?? [])}
            files={isSearching ? (searchQuery.data?.files ?? []) : (filesQuery.data?.files ?? [])}
            loading={isSearching ? searchQuery.isLoading : (filesQuery.isLoading || foldersQuery.isLoading)}
            onFolderOpen={openFolder}
            folderPathMap={isSearching ? folderPathMap : undefined}
          />
        </div>

      </div>
    </div>
  );
}
